import { useEffect } from 'react';
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
  useNavigate,
  useParams,
  useRouterState,
} from '@tanstack/react-router';
import { ory } from '@/auth';
import { DocumentHead } from '@/components/core/DocumentHead';
import { isSessionValid } from '@/helpers/auth';
import {
  getScheduledDowntimePhase,
  isAllowedDuringScheduledDowntime,
  isScheduledDowntimeStaff,
  normalizePathname,
} from '@/helpers/scheduledDowntime';
import { emailFromSession } from '@/helpers/studentAccess';
import { useScheduledDowntime } from '@/hooks/useScheduledDowntime';
import { useAuth } from '@/stores/auth';

const hasStaffSession = async (): Promise<boolean> => {
  const { session, setSession } = useAuth.getState();
  if (isSessionValid(session)) {
    return isScheduledDowntimeStaff(emailFromSession(session));
  }

  try {
    const { data: newSession } = await ory.toSession();
    if (!isSessionValid(newSession)) {
      return false;
    }
    setSession(newSession);
    return isScheduledDowntimeStaff(emailFromSession(newSession));
  } catch {
    return false;
  }
};

const getTenantIdParam = (params: object): string | undefined =>
  'tenantId' in params && typeof params.tenantId === 'string'
    ? params.tenantId
    : undefined;

export const Route = createRootRouteWithContext()({
  component: Root,
  beforeLoad: async ({ location, params }) => {
    const phase = getScheduledDowntimePhase();

    if (
      phase === 'active' &&
      !isAllowedDuringScheduledDowntime(location.pathname) &&
      !(await hasStaffSession())
    ) {
      throw redirect({
        to: '/downtime',
        replace: true,
        search: { tenantId: getTenantIdParam(params) },
      });
    }

    if (
      phase === 'after' &&
      normalizePathname(location.pathname) === '/downtime'
    ) {
      throw redirect({ to: '/', replace: true });
    }
  },
});

function Root() {
  const { phase } = useScheduledDowntime();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const { tenantId } = useParams({ strict: false });
  const navigate = useNavigate();

  useEffect(() => {
    if (phase === 'active' && !isAllowedDuringScheduledDowntime(pathname)) {
      let cancelled = false;
      void hasStaffSession().then((isStaff) => {
        if (!cancelled && !isStaff) {
          void navigate({
            to: '/downtime',
            replace: true,
            search: { tenantId },
          });
        }
      });
      return () => {
        cancelled = true;
      };
    }

    if (phase === 'after' && normalizePathname(pathname) === '/downtime') {
      void navigate({ to: '/', replace: true });
    }
  }, [navigate, pathname, phase, tenantId]);

  return (
    <div className="flex h-full">
      <DocumentHead
        titleKey="app.title"
        descriptionKey="app.description"
        resetOnUnmount={false}
      />
      <Outlet />
    </div>
  );
}
